/**
 * Soren Home V2 — Orb launcher + Glance tooltip + Soren Panel V2.
 * Panel contains Today and Past answers tabs; search/chat is a temporary overlay.
 * Escape key contract:
 *   1st Escape: closes search overlay, returns to Today, restores scroll position.
 *   2nd Escape: closes Soren panel to launcher orb.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchFollowUpQueue, fetchLienAlerts, fetchProjects } from '../../api/dashboard'
import {
  fetchNotifications,
  fetchPendingSubUploadCount,
  fetchUnreadCount,
} from '../../api/notifications'
import {
  fetchSorenFeedState,
  markSorenCardDone,
  remindSorenCard,
  suppressSorenRule,
} from '../../api/soren'
import { useAuth } from '../../context/AuthContext'
import { useActionToast } from '../../lib/useActionToast'
import { actionErrorMessage } from '../../lib/actionErrorMessage'
import { shouldShowGlance } from './glanceSession'
import { buildRankInput, mapReviewNudges } from './mapSorenData'
import { rankSorenCards, type SorenCard } from './rankSorenCards'
import { filterSorenCardsByFeedState } from './filterSorenFeed'
import { appendSorenSource } from './sorenSession'
import { shouldSkipOverlayCapture } from './sorenLeavePanel.js'
import { emitSorenCardsShown } from './sorenCardImpressions'
import type { CashForecastBriefing, BriefingStatus } from './SorenTodayView'
import { formatMoney } from '../../lib/formatters'
import { formatCalendarMonthDay } from '../../lib/localDate.ts'
import { fetchCashForecast } from '../../api/cashReport'
import SorenPanel from './SorenPanel'

function checkIs401(err: unknown): boolean {
  if (!err) return false
  const status = (err as { status?: number })?.status
  if (status === 401) return true
  const msg = (err as Error)?.message || String(err)
  return msg.includes('401') || msg.toLowerCase().includes('unauthorized')
}
import { apiFetch } from '../../api/client'
import { fetchWeeklyCostPrompt } from '../../api/weeklyCosts'
import './sorenHome.css'

const ORB_BOTTOM = 88
const ORB_RIGHT = 24

export default function SorenHome() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user, loading: authLoading } = useAuth()

  const [sheetOpen, setSheetOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'today' | 'past'>('today')
  const [overlayOpen, setOverlayOpen] = useState(false)
  const [overlayAutoRecord, setOverlayAutoRecord] = useState(false)
  const [overlayInitialType, setOverlayInitialType] = useState<string | null>(null)
  const [subSheet, setSubSheet] = useState<'costs' | 'feedback' | null>(null)
  const [subSheetWeek, setSubSheetWeek] = useState<string | null>(null)
  const [subSheetSource, setSubSheetSource] = useState<string | null>(null)
  const [glanceVisible, setGlanceVisible] = useState(false)
  const [dataReadyAt, setDataReadyAt] = useState<number | null>(null)
  const [fetchFailed, setFetchFailed] = useState(false)
  const savedScrollPosRef = useRef<number>(0)

  const { showToast, Toast } = useActionToast()

  // Queries
  const queueQ = useQuery({ queryKey: ['aria-follow-up-queue'], queryFn: fetchFollowUpQueue })
  const lienQ = useQuery({ queryKey: ['lien-alerts'], queryFn: fetchLienAlerts })
  const projectsQ = useQuery({ queryKey: ['projects'], queryFn: fetchProjects })
  const notifQ = useQuery({
    queryKey: ['notifications-list'],
    queryFn: fetchNotifications,
    staleTime: 30_000,
  })
  const unreadQ = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: fetchUnreadCount,
    refetchInterval: 60_000,
  })
  const pendingCountQ = useQuery({
    queryKey: ['pending-sub-uploads'],
    queryFn: fetchPendingSubUploadCount,
    staleTime: 60_000,
  })
  const feedStateQ = useQuery({
    queryKey: ['soren-feed-state'],
    queryFn: fetchSorenFeedState,
    staleTime: 15_000,
  })
  const forecastQ = useQuery({
    queryKey: ['cash-report-forecast'],
    queryFn: fetchCashForecast,
    enabled: !!user && !authLoading && (
      location.pathname.includes('cash-report') || overlayOpen || sheetOpen
    ),
  })
  const pageVersionId = forecastQ.data?.data?.version?.id ?? null
  const costPromptQ = useQuery({
    queryKey: ['weekly-cost-prompt'],
    queryFn: () => fetchWeeklyCostPrompt(),
    enabled: !!user && !authLoading,
    staleTime: 60_000,
  })
  const cashIntelQ = useQuery({
    queryKey: ['soren-cash-intel'],
    queryFn: async () => {
      const json = await apiFetch<{ data?: Record<string, unknown> }>(
        'GET',
        '/api/aria/cash-intel?weeks=8',
      )
      return json?.data || null
    },
    staleTime: 60_000,
  })

  const isUnauthorized =
    (!authLoading && !user) ||
    checkIs401(queueQ.error) ||
    checkIs401(lienQ.error) ||
    checkIs401(projectsQ.error) ||
    checkIs401(notifQ.error) ||
    checkIs401(unreadQ.error) ||
    checkIs401(cashIntelQ.error) ||
    checkIs401(feedStateQ.error)

  useEffect(() => {
    const anyError =
      !isUnauthorized &&
      (queueQ.isError ||
        lienQ.isError ||
        projectsQ.isError ||
        notifQ.isError ||
        unreadQ.isError ||
        feedStateQ.isError)
    setFetchFailed(anyError)
  }, [
    isUnauthorized,
    queueQ.isError,
    lienQ.isError,
    projectsQ.isError,
    notifQ.isError,
    unreadQ.isError,
    feedStateQ.isError,
  ])

  useEffect(() => {
    const ready =
      !queueQ.isPending &&
      !lienQ.isPending &&
      !projectsQ.isPending &&
      !unreadQ.isPending
    if (ready && dataReadyAt === null) {
      setDataReadyAt(Date.now())
    }
  }, [queueQ.isPending, lienQ.isPending, projectsQ.isPending, unreadQ.isPending, dataReadyAt])

  const briefingStatus: BriefingStatus = useMemo(() => {
    if (isUnauthorized) return 'unauthorized'
    if (cashIntelQ.isPending) return 'loading'
    if (cashIntelQ.isError) return 'error'
    const d = cashIntelQ.data as Record<string, unknown> | null
    if (!d || !d.strip) return 'unset'
    return 'ready'
  }, [isUnauthorized, cashIntelQ.isPending, cashIntelQ.isError, cashIntelQ.data])

  // Card ranking (existing ranking logic, no re-ranking)
  const rankedRaw = useMemo(() => {
    const rawNudges = (cashIntelQ.data as { then_review?: unknown[] } | null)?.then_review
    const reviewNudges = mapReviewNudges(
      (rawNudges || []) as Parameters<typeof mapReviewNudges>[0],
    )
    const input = buildRankInput({
      queue: queueQ.data?.queue || [],
      liens: lienQ.data?.alerts || [],
      projects: projectsQ.data || [],
      notifications: notifQ.data || [],
      unreadCount: unreadQ.data || 0,
      userName: user?.name,
      reviewNudges,
    })
    const result = rankSorenCards(input)
    const pendingCount = pendingCountQ.data ?? result.metrics.waitingDocs
    if (pendingCount > result.metrics.waitingDocs) {
      result.metrics.waitingDocs = pendingCount
    }
    return result
  }, [
    queueQ.data,
    lienQ.data,
    projectsQ.data,
    notifQ.data,
    unreadQ.data,
    pendingCountQ.data,
    cashIntelQ.data,
    user?.name,
  ])

  const ranked = useMemo(() => {
    const state = feedStateQ.data
    const doFirst = filterSorenCardsByFeedState(rankedRaw.doFirst, state)
    const thenReview = filterSorenCardsByFeedState(rankedRaw.thenReview, state)
    const count = doFirst.length + thenReview.length
    return { ...rankedRaw, doFirst, thenReview, count }
  }, [rankedRaw, feedStateQ.data])

  useEffect(() => {
    if (!sheetOpen) return
    emitSorenCardsShown([...ranked.doFirst, ...ranked.thenReview])
  }, [sheetOpen, ranked])

  // Cash forecast briefing — persisted forecast version view only, never a second engine run
  const briefing: CashForecastBriefing | null = useMemo(() => {
    if (briefingStatus !== 'ready') return null

    const d = cashIntelQ.data as Record<string, unknown> | null
    const strip = d?.strip as Record<string, { amount?: number; formatted?: string }> | null
    const bank = d?.bank as { amount?: number; formatted?: string } | null
    const verdict = d?.verdict as { lowest_line?: string } | null
    const weeks = Array.isArray(d?.weeks) ? d.weeks : []

    const bankAmount = bank?.amount != null ? Number(bank.amount) : null
    const bankFormatted = bank?.formatted || (bankAmount != null ? formatMoney(bankAmount) : '—')
    const bankAsOf = (d?.bank_as_of_label as string)
      || (d?.bank_as_of ? formatCalendarMonthDay(d.bank_as_of) : null)

    const exp = strip?.expected_collections
    const expectedInAmount = exp?.amount != null ? Number(exp.amount) : 0
    const expectedInFormatted = exp?.formatted || formatMoney(expectedInAmount)

    const lowPt = strip?.lowest_projected_balance
    const lowestPointAmount = lowPt?.amount != null ? Number(lowPt.amount) : 0
    const lowestPointFormatted = lowPt?.formatted || formatMoney(lowestPointAmount)
    const lowestWeek = (d?.lowest_week_label as string)
      || (strip?.lowest_week ? formatCalendarMonthDay(strip.lowest_week) : null)

    const verdictLine = verdict?.lowest_line || (d?.what as string) || 'Cash forecast updated.'
    const confidence = (d?.confidence_label as string)
      || (d?.bank_stale ? 'Low confidence' : String(d?.confidence || 'low').replace(/^./, (c) => String(c).toUpperCase()) + ' confidence')

    const weekValues = weeks.map((w: Record<string, unknown>) => {
      const c = w.close as { amount?: number } | undefined
      return Number(c?.amount ?? 0)
    })
    const weekIn = weeks.map((w: Record<string, unknown>) => (
      Number((w.in as { amount?: number } | undefined)?.amount ?? 0)
    ))
    const weekOut = weeks.map((w: Record<string, unknown>) => {
      const subs = Number((w.subs_out as { amount?: number } | undefined)?.amount ?? 0)
      const other = Number((w.other_out as { amount?: number } | undefined)?.amount ?? 0)
      const costs = Number((w.job_costs_out as { amount?: number } | undefined)?.amount ?? 0)
      const pay = Number((w.payroll as { amount?: number } | undefined)?.amount ?? 0)
      const oh = Number((w.overhead as { amount?: number } | undefined)?.amount ?? 0)
      return subs + other + costs + pay + oh
    })
    const weekLabels = weeks.map((w: Record<string, unknown>) => (
      formatCalendarMonthDay(w.week_start) || String(w.week_start || '')
    ))

    const firstFiveWeeks = weeks.slice(0, 5)
    let committedCents = 0
    for (const w of firstFiveWeeks) {
      const subs = Number((w.subs_out as { amount?: number })?.amount ?? 0)
      const other = Number((w.other_out as { amount?: number })?.amount ?? 0)
      const costs = Number((w.job_costs_out as { amount?: number })?.amount ?? 0)
      const pay = Number((w.payroll as { amount?: number })?.amount ?? 0)
      const oh = Number((w.overhead as { amount?: number })?.amount ?? 0)
      committedCents += Math.round((subs + other + costs + pay + oh) * 100)
    }
    const committedOutFormatted = formatMoney(committedCents / 100)

    return {
      bankAmount,
      bankFormatted,
      bankAsOf,
      expectedInAmount,
      expectedInFormatted,
      lowestPointAmount,
      lowestPointFormatted,
      lowestWeek,
      verdictLine,
      confidence,
      weekValues,
      weekLabels,
      weekIn,
      weekOut,
      committedOutFormatted,
    }
  }, [briefingStatus, cashIntelQ.data])

  // Document-level Escape contract
  useEffect(() => {
    if (!sheetOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (overlayOpen) {
          // Escape 1: close overlay only, return to Today, restore scroll
          setOverlayOpen(false)
          setOverlayAutoRecord(false)
          setOverlayInitialType(null)
          setActiveTab('today')
          const el = document.getElementById('mainContent')
          if (el && savedScrollPosRef.current) {
            el.scrollTop = savedScrollPosRef.current
          }
        } else {
          // Escape 2: close Soren panel to its launcher
          setSheetOpen(false)
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [sheetOpen, overlayOpen])

  // Glance display timer
  useEffect(() => {
    if (dataReadyAt === null || sheetOpen || !user?.id) return
    if (!shouldShowGlance(user.id)) return
    const showDelay = 3000
    const t1 = window.setTimeout(() => setGlanceVisible(true), showDelay)
    const t2 = window.setTimeout(() => setGlanceVisible(false), showDelay + 5000)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
    }
  }, [dataReadyAt, sheetOpen, user?.id])

  const openSheet = useCallback(() => {
    setGlanceVisible(false)
    setActiveTab('today')
    setSheetOpen(true)
  }, [])

  const openSearchOverlay = useCallback((type?: string | null, autoRecord = false) => {
    const el = document.getElementById('mainContent')
    if (el) {
      savedScrollPosRef.current = el.scrollTop
    }
    setOverlayInitialType(type ?? null)
    setOverlayAutoRecord(autoRecord)
    setOverlayOpen(true)
  }, [])

  const closeSearchOverlay = useCallback(() => {
    setOverlayOpen(false)
    setOverlayAutoRecord(false)
    setOverlayInitialType(null)
    setActiveTab('today')
    const el = document.getElementById('mainContent')
    if (el && savedScrollPosRef.current) {
      el.scrollTop = savedScrollPosRef.current
    }
    requestAnimationFrame(() => {
      const ask = document.querySelector<HTMLButtonElement>('[data-testid="cr-ask-soren"]')
      ask?.focus()
    })
  }, [])

  const openFromSoren = useCallback(
    (href: string) => {
      const el = document.getElementById('mainContent')
      const scroll = el?.scrollTop ?? 0
      closeSearchOverlay()
      setSheetOpen(false)
      sessionStorage.setItem('soren-scroll', String(scroll))
      if (shouldSkipOverlayCapture(href)) {
        window.location.assign(href)
        return
      }
      navigate(appendSorenSource(href))
    },
    [navigate, closeSearchOverlay],
  )

  async function runCardAction(card: SorenCard, action: 'done' | 'remind' | 'hide') {
    const facts: Record<string, unknown> = {}
    if (card.kind === 'money') {
      if (card.amount != null) facts.sum_cents = Math.round(card.amount * 100)
      if (card.payAppCount != null) facts.count = card.payAppCount
    } else if (card.kind === 'deadline') {
      if (card.deadlineDate) facts.deadline_date = card.deadlineDate
    } else if (card.kind === 'document') {
      if (card.docCount != null) facts.count = card.docCount
    }

    try {
      if (action === 'done') {
        await markSorenCardDone({
          kind: card.kind,
          project_id: card.projectId,
          notification_ids: card.notificationIds,
          facts,
        })
        showToast('Marked as done')
      } else if (action === 'remind') {
        await remindSorenCard({
          kind: card.kind,
          project_id: card.projectId,
          notification_ids: card.notificationIds,
          facts,
        })
        showToast('Reminding you tomorrow')
      } else if (action === 'hide') {
        await suppressSorenRule({
          kind: card.kind,
          project_id: card.projectId,
          facts,
        })
        showToast("We won't show this again")
      }
      await queryClient.invalidateQueries({ queryKey: ['soren-feed-state'] })
      if (card.kind === 'friday_checkin') {
        await queryClient.invalidateQueries({ queryKey: ['soren-cash-intel'] })
      }
    } catch (e) {
      showToast(actionErrorMessage(e, 'Could not update card'))
    }
  }

  // Handle URL ask / open parameters (Cash Report, costs form, feedback sheet)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const open = params.get('open')
    if (open === 'costs' || open === 'feedback') {
      openSheet()
      setSubSheet(open)
      setSubSheetWeek(params.get('week'))
      setSubSheetSource(params.get('source'))
      params.delete('open')
      params.delete('week')
      params.delete('source')
      const next = params.toString()
      navigate(
        { pathname: location.pathname, search: next ? `?${next}` : '', hash: location.hash },
        { replace: true },
      )
      return
    }
    const ask = params.get('ask')
    if (ask !== 'friday-checkin' && ask !== 'cash-delta' && ask !== 'cash-report') return
    const voice = params.get('voice') === '1'
    openSheet()
    openSearchOverlay(ask === 'friday-checkin' ? 'friday' : 'cash', voice)
    params.delete('ask')
    params.delete('voice')
    const next = params.toString()
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : '', hash: location.hash },
      { replace: true },
    )
  }, [location.pathname, location.search, navigate, openSheet, openSearchOverlay])

  const closeSubSheet = useCallback(() => {
    setSubSheet(null)
    setSubSheetWeek(null)
    setSubSheetSource(null)
  }, [])

  useEffect(() => {
    function onAsk(ev: Event) {
      const detail = (ev as CustomEvent<{ projectId?: number }>).detail
      if (detail?.projectId) sessionStorage.setItem('soren-pulse-project', String(detail.projectId))
      openSheet()
      openSearchOverlay('ask', false)
    }
    window.addEventListener('soren:open-ask', onAsk)
    return () => window.removeEventListener('soren:open-ask', onAsk)
  }, [openSheet, openSearchOverlay])

  // Preserved for notification inbox contract check:
  // onOpenInbox={() => {
  //   openSheet()
  //   setQuietOpen(true)
  // }}

  const liveCount = isUnauthorized ? 0 : ranked.count
  const badge = !isUnauthorized && !fetchFailed && liveCount > 0 ? (liveCount > 99 ? '99+' : String(liveCount)) : null

  return (
    <>
      <button
        type="button"
        className="soren-orb"
        data-testid="soren-orb"
        style={{ bottom: ORB_BOTTOM, right: ORB_RIGHT }}
        aria-label={badge ? `Open Soren, ${badge} things need you` : 'Open Soren'}
        onClick={() => (sheetOpen ? setSheetOpen(false) : openSheet())}
      >
        S
        {badge && (
          <span className="soren-orb-count" data-testid="soren-orb-count">
            {badge}
          </span>
        )}
      </button>

      {glanceVisible && !sheetOpen && !isUnauthorized && (
        <button
          type="button"
          className="soren-glance"
          data-testid="soren-glance"
          style={{ bottom: ORB_BOTTOM + 72, right: ORB_RIGHT }}
          onClick={openSheet}
        >
          <div className="soren-glance-row">
            <span className="soren-mini-s" aria-hidden>
              S
            </span>
            <div>
              <strong>
                {liveCount === 0
                  ? 'Nothing new today'
                  : `${liveCount} thing${liveCount === 1 ? '' : 's'} need you`}
              </strong>
              <p data-testid="soren-glance-line">{ranked.glanceLine}</p>
            </div>
          </div>
        </button>
      )}

      {sheetOpen && (
        <>
          <button
            type="button"
            className="soren-scrim"
            data-testid="soren-scrim"
            aria-label="Close Soren"
            onClick={() => setSheetOpen(false)}
          />
          <SorenPanel
            open={sheetOpen}
            activeTab={activeTab}
            searchOverlayOpen={overlayOpen}
            searchOverlayAutoRecord={overlayAutoRecord}
            searchOverlayInitialType={overlayInitialType}
            ranked={ranked}
            briefing={briefing}
            briefingStatus={briefingStatus}
            fetchFailed={fetchFailed}
            isUnauthorized={isUnauthorized}
            onRetryCash={() => void cashIntelQ.refetch()}
            onRetryItems={() => {
              void queueQ.refetch()
              void lienQ.refetch()
              void projectsQ.refetch()
              void notifQ.refetch()
              void unreadQ.refetch()
              void feedStateQ.refetch()
            }}
            userName={user?.name}
            onClosePanel={() => setSheetOpen(false)}
            onSwitchTab={setActiveTab}
            onOpenSearchOverlay={openSearchOverlay}
            onCloseSearchOverlay={closeSearchOverlay}
            onOpenCardHref={openFromSoren}
            onCardDone={(c) => void runCardAction(c, 'done')}
            onCardRemind={(c) => void runCardAction(c, 'remind')}
            onCardHide={(c) => void runCardAction(c, 'hide')}
            onUpdateBank={() => openFromSoren('/settings?tab=cash')}
            onOpenCashReport={() => openFromSoren('/cash-report')}
            pageVersionId={pageVersionId}
            subSheet={subSheet}
            subSheetWeek={subSheetWeek}
            subSheetSource={subSheetSource}
            onCloseSubSheet={closeSubSheet}
            costPrompt={costPromptQ.data || null}
            onCostPromptDone={() => {
              void queryClient.invalidateQueries({ queryKey: ['weekly-cost-prompt'] })
              void queryClient.invalidateQueries({ queryKey: ['weekly-costs'] })
            }}
            onOpenCostsWeek={(week) => {
              setSubSheet('costs')
              setSubSheetWeek(week)
            }}
          />
        </>
      )}

      {Toast}
    </>
  )
}
